import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import {
  LayoutDashboard, MapPin, QrCode, ClipboardList, BarChart2,
  Users, MessageSquare, LogOut, Menu, X, CheckSquare,
  Building2, FileText, User, ChevronDown, Shield, Sun, Moon, Globe
} from 'lucide-react';

const NAV = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/kurumlar', label: 'Kurumlar', icon: Shield, roller: ['superadmin'] },
  { to: '/binalar', label: 'Binalar', icon: Building2, roller: ['superadmin','yonetici'] },
  { to: '/lokasyonlar', label: 'Lokasyonlar', icon: MapPin, roller: ['superadmin','yonetici'] },
  { to: '/anketler', label: 'Anket Şablonları', icon: FileText, roller: ['superadmin','yonetici'] },
  { to: '/checklistler', label: 'Checklist Şablonları', icon: CheckSquare, roller: ['superadmin','yonetici'] },
  { to: '/qr-yonetim', label: 'QR Yönetim', icon: QrCode, roller: ['superadmin','yonetici'] },
  { to: '/anket-sonuclar', label: 'Anket Sonuçları', icon: ClipboardList },
  { to: '/checklist-kayitlar', label: 'Checklist Kayıtlar', icon: CheckSquare },
  { to: '/raporlar', label: 'Raporlar', icon: BarChart2 },
  { to: '/kullanicilar', label: 'Kullanıcılar', icon: Users, roller: ['superadmin','yonetici'] },
  { to: '/sms-ayarlari', label: 'SMS Ayarları', icon: MessageSquare, roller: ['superadmin','yonetici'] },
  { to: '/site-ayarlari', label: 'Site Ayarları', icon: Globe, roller: ['superadmin'] },
  { to: '/profil', label: 'Profilim', icon: User },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem('kc_theme') === 'dark');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
    localStorage.setItem('kc_theme', dark ? 'dark' : 'light');
  }, [dark]);

  const handleLogout = () => { logout(); navigate('/giris'); };

  const avatarSrc = user?.profil_resmi
    ? `${API.replace('/api','')}/uploads/profil/${user.profil_resmi}`
    : null;

  const kurumLogoSrc = user?.kurum_logo
    ? `${API.replace('/api','')}/uploads/${user.kurum_logo}`
    : null;

  const visibleNav = NAV.filter(item => !item.roller || item.roller.includes(user?.rol));

  const SidebarContent = ({ onClose }) => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Logo / Kurum */}
      <div className="sidebar-logo-area">
        {kurumLogoSrc
          ? <img src={kurumLogoSrc} alt="logo" style={{ width: 32, height: 32, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
          : <img src="/logo.png" alt="temiz-iz" style={{ width: 32, height: 32, borderRadius: 8, objectFit: 'contain', flexShrink: 0 }} onError={e => e.target.style.opacity='0'} />
        }
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: 13, lineHeight: 1.3 }}>temiz-iz.com</div>
          {user?.kurum_adi && (
            <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user.kurum_adi}
            </div>
          )}
        </div>
        {onClose && (
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', padding: 4, flexShrink: 0 }}>
            <X size={16} />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="sidebar-nav">
        {visibleNav.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onClose}
            className={({ isActive }) => `sidebar-link${isActive ? ' active' : ''}`}
          >
            <item.icon className="nav-icon" />
            {item.label}
          </NavLink>
        ))}
      </nav>

      {/* User footer */}
      <div className="sidebar-footer">
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 10px', borderRadius: 8, cursor: 'pointer', marginBottom: 4, transition: 'background 0.15s' }}
          onClick={() => { navigate('/profil'); onClose?.(); }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          {avatarSrc
            ? <img src={avatarSrc} alt="" style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(255,255,255,0.15)', flexShrink: 0 }} />
            : <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 700, flexShrink: 0, border: '2px solid rgba(255,255,255,0.12)' }}>
                {user?.ad?.[0]}{user?.soyad?.[0]}
              </div>
          }
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: '#fff', fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.ad} {user?.soyad}</div>
            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, textTransform: 'capitalize' }}>{user?.rol}</div>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="sidebar-link"
          style={{ color: 'rgba(252,165,165,0.8)', marginTop: 2 }}
        >
          <LogOut className="nav-icon" />
          Çıkış Yap
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      {/* Desktop Sidebar */}
      <aside className="sidebar">
        <SidebarContent />
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 40, background: 'rgba(0,0,0,0.5)' }} onClick={() => setMobileOpen(false)} />
          <aside className="sidebar open" style={{ zIndex: 60 }}>
            <SidebarContent onClose={() => setMobileOpen(false)} />
          </aside>
        </>
      )}

      {/* Main */}
      <div className="main-content" style={{ flex: 1 }}>
        {/* Topbar */}
        <header className="topbar">
          {/* Mobile menu btn */}
          <button className="mobile-only btn btn-ghost btn-icon" onClick={() => setMobileOpen(true)}>
            <Menu size={18} />
          </button>

          {/* Kurum adı (topbar'da) */}
          {user?.kurum_adi && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {kurumLogoSrc && <img src={kurumLogoSrc} alt="" style={{ width: 22, height: 22, borderRadius: 6, objectFit: 'cover' }} />}
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{user.kurum_adi}</span>
            </div>
          )}

          <div style={{ flex: 1 }} />

          {/* Dark mode toggle */}
          <button className="theme-toggle" onClick={() => setDark(d => !d)} title={dark ? 'Aydınlık mod' : 'Karanlık mod'}>
            {dark ? <Sun size={14} /> : <Moon size={14} />}
          </button>

          {/* User */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setUserMenu(v => !v)}
              style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '5px 10px', borderRadius: 8, border: '1.5px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}
            >
              {avatarSrc
                ? <img src={avatarSrc} alt="" style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover' }} />
                : <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'linear-gradient(135deg,#1d4ed8,#4a6cf7)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
                    {user?.ad?.[0]}{user?.soyad?.[0]}
                  </div>
              }
              <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{user?.ad} {user?.soyad}</span>
              <ChevronDown size={12} color="var(--text-muted)" />
            </button>
            {userMenu && (
              <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setUserMenu(false)} />
                <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)', boxShadow: 'var(--shadow-lg)', minWidth: 170, zIndex: 100, overflow: 'hidden' }}>
                  <div style={{ padding: '8px 14px', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{user?.ad} {user?.soyad}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{user?.email}</div>
                  </div>
                  <button onClick={() => { setUserMenu(false); navigate('/profil'); }} style={{ width: '100%', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, color: 'var(--text-secondary)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
                    <User size={14} /> Profil
                  </button>
                  <button onClick={handleLogout} style={{ width: '100%', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, color: 'var(--danger)', background: 'none', border: 'none', cursor: 'pointer', borderTop: '1px solid var(--border)', fontFamily: 'inherit' }}>
                    <LogOut size={14} /> Çıkış Yap
                  </button>
                </div>
              </>
            )}
          </div>
        </header>

        <div className="page-content">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
